import { StaffTicket, TicketComment, TicketUserRef } from "@signalist/shared-types";

import { lastTicketActivity, TicketActivityKind } from "../../Support/utils/ticketRowMeta";

export const ticketUserName = (ref: TicketUserRef | null | undefined): string =>
	ref ? [ref.firstName, ref.lastName].filter(Boolean).join(" ") || ref.email : "";

/**
 * Who wrote the ticket. A mail from an address matching no user has no `createdBy` row, so the
 * raw sender is the name (PREC-3978) — without this the staff queue would show a blank cell.
 */
export const ticketSenderName = (
	ticket: Pick<StaffTicket, "createdBy" | "externalSenderName" | "externalSenderEmail">
): string =>
	ticketUserName(ticket.createdBy) || ticket.externalSenderName || ticket.externalSenderEmail || "";

/**
 * Who wrote one comment. A mailed reply from an address matching no user (or matching a user who
 * is not the ticket's creator) has no `author` row, so the sender's own address is the name
 * (PREC-4002) — without this a comment like that renders with no author at all.
 */
export const ticketCommentAuthorName = (
	comment: Pick<TicketComment, "author" | "externalAuthorEmail">
): string => ticketUserName(comment.author) || comment.externalAuthorEmail || "";

export const ticketUserInitials = (ref: TicketUserRef | null | undefined): string => {
	if (!ref) {
		return "";
	}

	const fromNames = [ref.firstName, ref.lastName]
		.filter((part): part is string => Boolean(part))
		.map((part) => part[0].toUpperCase())
		.join("");

	return fromNames || ref.email[0].toUpperCase();
};

export interface LastActivityActor {
	kind: "CUSTOMER" | "FILED" | "STAFF" | "CLOSED";
	/** The assignee's first name for a staff action; absent = "Signalist". */
	name?: string;
}

export interface StaffLastActivity {
	at: string;
	actor: LastActivityActor;
}

/**
 * The "Last activity" cell from the staff side. The row does not say who replied, so a staff
 * action is credited to the assignee (the mockup does the same) and to Signalist when unassigned.
 */
export const staffLastActivity = (ticket: StaffTicket): StaffLastActivity => {
	const activity = lastTicketActivity(ticket);

	switch (activity.kind) {
		case TicketActivityKind.CUSTOMER_REPLIED:
			return { at: activity.at, actor: { kind: "CUSTOMER" } };
		case TicketActivityKind.SUPPORT_REPLIED:
		case TicketActivityKind.RESOLVED:
			return {
				at: activity.at,
				actor: {
					kind: "STAFF",
					...(ticket.assignee?.firstName ? { name: ticket.assignee.firstName } : {}),
				},
			};
		case TicketActivityKind.CLOSED:
			return { at: activity.at, actor: { kind: "CLOSED" } };
		default:
			return { at: activity.at, actor: { kind: "FILED" } };
	}
};
